import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../../lib/api";
import { CreditCard, Zap, Check, ArrowRight, Loader2, Receipt, Crown, Ticket } from "lucide-react";

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-2xl border border-slate-200 shadow-card ${className}`}>{children}</div>
);

export default function CandidateBilling() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [plans, setPlans] = useState(null);

  useEffect(() => {
    api.get("/billing/me").then((r) => setMe(r.data));
    api.get("/billing/plans").then((r) => setPlans(r.data));
  }, []);
  if (!me || !plans) return <div className="min-h-[40vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;

  const plan = me.subscription?.plan || "free";
  const cur = plans.currency;
  const pro = plans.individual_plans.find((p) => p.id === "pro");
  const payg = plans.individual_plans.find((p) => p.id === "payg");
  const isPro = plan === "pro";

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <div className="text-xs font-bold uppercase tracking-wider text-brand mb-1">Billing & Plan</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-ink">Your Subscription</h1>
      </div>

      {/* Current plan */}
      <div className="grid sm:grid-cols-3 gap-4">
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-2"><Crown className={`h-5 w-5 ${isPro ? "text-amber-500" : "text-ink-faint"}`} /><span className="text-xs font-bold uppercase tracking-wider text-ink-faint">Current Plan</span></div>
          <div className="font-heading text-2xl font-bold text-ink capitalize" data-testid="current-plan">{isPro ? "Pro" : plan === "payg" ? "Pay Per Interview" : "Free"}</div>
          {isPro && me.subscription?.valid_until && <div className="text-xs text-ink-faint mt-1 num">Renews {me.subscription.valid_until}</div>}
          {!isPro && <div className="text-xs text-ink-faint mt-1 num">{me.free_ai_used}/{me.free_ai_quota} free AI interviews used</div>}
        </Card>
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-2"><Ticket className="h-5 w-5 text-brand" /><span className="text-xs font-bold uppercase tracking-wider text-ink-faint">Interview Wallet</span></div>
          <div className="flex gap-4">
            <div><div className="num font-bold text-2xl text-ink" data-testid="wallet-ai">{me.wallet.ai}</div><div className="text-xs text-ink-faint">AI</div></div>
            <div><div className="num font-bold text-2xl text-ink" data-testid="wallet-expert">{me.wallet.expert}</div><div className="text-xs text-ink-faint">Expert</div></div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-2"><Receipt className="h-5 w-5 text-brand" /><span className="text-xs font-bold uppercase tracking-wider text-ink-faint">Invoices</span></div>
          <div className="num font-bold text-2xl text-ink">{me.invoices.length}</div>
          <div className="text-xs text-ink-faint">total payments</div>
        </Card>
      </div>

      {/* Upgrade / buy */}
      {!isPro && (
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="p-6 bg-slate-900 text-white border-slate-900">
            <div className="flex items-center gap-2 mb-1"><Crown className="h-5 w-5 text-amber-400" /><span className="font-heading text-xl font-bold">Pro</span></div>
            <div className="num font-extrabold text-3xl">{cur}{pro.price.toLocaleString()}<span className="text-sm text-blue-200">/mo</span></div>
            <ul className="mt-4 space-y-1.5">{pro.features.slice(0, 5).map((f) => <li key={f} className="flex items-center gap-2 text-sm text-blue-50"><Check className="h-4 w-4 text-white" /> {f}</li>)}</ul>
            <button data-testid="upgrade-pro" onClick={() => navigate("/checkout?kind=pro")} className="mt-5 w-full inline-flex items-center justify-center gap-2 text-sm font-bold text-brand bg-white hover:bg-blue-50 px-5 py-3 rounded-full transition-colors">Upgrade to Pro <ArrowRight className="h-4 w-4" /></button>
          </Card>
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-1"><Zap className="h-5 w-5 text-brand" /><span className="font-heading text-xl font-bold text-ink">Pay Per Interview</span></div>
            <p className="text-sm text-ink-soft mb-4">Buy single interviews without a subscription.</p>
            <div className="space-y-2">
              <button data-testid="buy-ai" onClick={() => navigate("/checkout?kind=payg_ai")} className="w-full flex items-center justify-between rounded-xl border border-slate-200 hover:border-brand px-4 py-3 transition-colors"><span className="text-sm font-semibold text-ink">AI Interview</span><span className="num font-bold text-brand">{cur}{payg.payg_prices.ai}</span></button>
              <button data-testid="buy-expert" onClick={() => navigate("/checkout?kind=payg_expert")} className="w-full flex items-center justify-between rounded-xl border border-slate-200 hover:border-brand px-4 py-3 transition-colors"><span className="text-sm font-semibold text-ink">Expert Interview</span><span className="num font-bold text-brand">{cur}{payg.payg_prices.expert}</span></button>
            </div>
          </Card>
        </div>
      )}

      {/* Billing history */}
      <Card className="p-6">
        <div className="font-heading font-bold text-ink mb-4 flex items-center gap-2"><Receipt className="h-4 w-4 text-brand" /> Billing History</div>
        {me.invoices.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-faint border-b border-slate-200"><th className="px-3 py-2.5">Invoice</th><th className="px-3 py-2.5">Description</th><th className="px-3 py-2.5">Date</th><th className="px-3 py-2.5">Amount</th><th className="px-3 py-2.5 text-right"></th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {me.invoices.map((inv) => (
                  <tr key={inv.id} data-testid={`invoice-${inv.id}`}>
                    <td className="px-3 py-3 num text-brand font-semibold">{inv.id}</td>
                    <td className="px-3 py-3 text-ink">{inv.description}</td>
                    <td className="px-3 py-3 num text-ink-soft">{new Date(inv.date).toLocaleDateString()}</td>
                    <td className="px-3 py-3 num font-bold text-ink">{inv.currency}{inv.amount.toLocaleString()}</td>
                    <td className="px-3 py-3 text-right"><Link to={`/invoice/${inv.id}`} className="text-xs font-bold text-brand">View</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <div className="text-sm text-ink-soft py-6 text-center">No payments yet.</div>}
      </Card>
    </div>
  );
}
